/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { AppConfigService, AuthenticationService } from '@alfresco/adf-core';
import { NodesApi, UploadApi } from '@alfresco/js-api';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { DmsMetadataService } from '../upload/dms-metadata.service';
import { LaunchedFlow, NO_ASSIGNEE, WorkflowService } from '../workflow/workflow.service';
import { FormDefinition, FormProblemCode, FormValues, pdfName, storedProperties, workflowValues } from './forms.model';
import { FormSubmission } from './forms.report';

/** A printed form: the PDF, the reference number on it, and whether the organisation signed it. */
export interface PrintedForm {
  pdf: Blob;
  reference: string;
  signed: boolean;
}

/** A form filed in the repository. */
export interface SubmittedForm {
  nodeId: string;
  name: string;
  reference: string;
  signed: boolean;
}

/** Why a filed form's flow did not start: gone from the catalogue, nobody on its first step, or the engine refused. */
export type FlowProblem = 'missing' | 'noAssignee' | 'failed';

/** Why printing failed: the values (with the field each problem is in), the engine, or something else. */
export interface FormPrintError {
  kind: 'values' | 'engine' | 'other';
  problems?: Record<string, FormProblemCode>;
  message?: string;
}

/**
 * Forms: definitions are plain JSON files in the Data Dictionary, read and
 * written with the person's own session. The jobs service prints the PDF —
 * it reads the definition itself, so what a person submits is always the
 * published wording — and the PDF is then stored here, as the person, in the
 * folder the form names.
 */
@Injectable({ providedIn: 'root' })
export class FormsService {
  private readonly api = inject(AlfrescoApiService);
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(AppConfigService);
  private readonly auth = inject(AuthenticationService);
  private readonly metadata = inject(DmsMetadataService);
  private readonly workflow = inject(WorkflowService);

  static readonly FOLDER = 'Data Dictionary/ACA/forms';
  /** Paper forms' PDFs: inside the forms folder, where the jobs service looks for them. */
  static readonly PAPER_FOLDER = `${FormsService.FOLDER}/paper`;

  private get nodes(): NodesApi {
    return new NodesApi(this.api.getInstance() as any);
  }

  private get uploads(): UploadApi {
    return new UploadApi(this.api.getInstance() as any);
  }

  private get baseUrl(): string {
    return `${this.appConfig.get<string>('jobs.baseUrl', '/jobs-api')}/api/forms`;
  }

  // ── definitions ───────────────────────────────────────────────────────

  /** Every form the person can read; none when the folder does not exist yet. */
  list(): Observable<FormDefinition[]> {
    return from(this.nodes.getNode('-root-', { relativePath: FormsService.FOLDER } as any) as Promise<any>).pipe(
      switchMap((folder: any) => from(this.nodes.listNodeChildren(folder.entry.id, { maxItems: 500 } as any) as Promise<any>)),
      switchMap((res: any) => {
        const files: any[] = (res?.list?.entries || []).map((e: any) => e.entry).filter((n: any) => n.isFile && n.name?.endsWith('.json'));
        return files.length ? forkJoin(files.map((f: any) => this.read(f.id).pipe(catchError(() => of(null))))) : of([]);
      }),
      map((forms) => (forms as (FormDefinition | null)[]).filter((f): f is FormDefinition => !!f)),
      map((forms) => forms.sort((a, b) => a.title.localeCompare(b.title, 'th'))),
      catchError((error) => (error?.status === 404 ? of([]) : throwError(() => error)))
    );
  }

  get(id: string): Observable<FormDefinition> {
    return this.read(id).pipe(switchMap((form) => (form ? of(form) : throwError(() => new Error('The form file cannot be read.')))));
  }

  /** Creates the file for a new form or overwrites an existing one. Answers the saved form, with its id. */
  save(form: FormDefinition): Observable<FormDefinition> {
    const { id, ...definition } = form;
    const saved: FormDefinition = { ...definition, updatedAt: new Date().toISOString(), updatedBy: this.auth.getUsername() ?? '' };
    const json = JSON.stringify(saved, null, 2);
    if (id) {
      return from(this.nodes.updateNodeContent(id, json) as Promise<any>).pipe(map(() => ({ ...saved, id })));
    }
    return this.folderId().pipe(
      switchMap((folderId) => from(this.nodes.createNode(folderId, { name: this.fileName(form), nodeType: 'cm:content' } as any) as Promise<any>)),
      switchMap((res: any) => from(this.nodes.updateNodeContent(res.entry.id, json) as Promise<any>).pipe(map(() => res.entry.id as string))),
      map((newId) => ({ ...saved, id: newId }))
    );
  }

  /** Stores a paper form's PDF where the jobs service will read it. Answers its node id and the name it was stored under. */
  uploadPaper(pdf: Blob, name: string): Observable<{ nodeId: string; name: string }> {
    const file = new File([pdf], name.replace(/\.[^.]*$/, '') + '.pdf', { type: 'application/pdf' });
    return this.folderId(FormsService.PAPER_FOLDER).pipe(
      switchMap((folderId) =>
        from(this.uploads.uploadFile(file, '', folderId, null, { name: file.name, nodeType: 'cm:content', autoRename: true }) as Promise<any>)
      ),
      map((res: any) => ({ nodeId: res.entry.id as string, name: res.entry.name as string }))
    );
  }

  /** A paper form's PDF, for the builder to show its pages. */
  paperPdf(nodeId: string): Observable<ArrayBuffer> {
    return from(this.nodes.getNodeContent(nodeId) as Promise<any>).pipe(
      switchMap((data: any) =>
        from(typeof data?.arrayBuffer === 'function' ? (data.arrayBuffer() as Promise<ArrayBuffer>) : Promise.resolve(data as ArrayBuffer))
      )
    );
  }

  /** Only a form nobody has filed anything with should go; the list offers archiving instead. */
  remove(id: string): Observable<unknown> {
    return from(this.nodes.deleteNode(id) as Promise<any>);
  }

  // ── printing and filing ───────────────────────────────────────────────

  /** The PDF of a published form, filled in with these values; signed when it is being filed and the form says so. */
  print(formId: string, values: FormValues, submit = false): Observable<PrintedForm> {
    return this.pdf(`${this.baseUrl}/${encodeURIComponent(formId)}/render`, { values, submit });
  }

  /** The builder's preview of a form as it stands, saved or not (administrators only). */
  preview(form: FormDefinition, values: FormValues): Observable<PrintedForm> {
    const definition: FormDefinition = { ...form };
    delete definition.id;
    return this.pdf(`${this.baseUrl}/preview`, { definition, values });
  }

  /** Prints the form, stores the PDF where the form says, and files it. The flow, if any, is {@link startWorkflow}'s. */
  submit(form: FormDefinition, values: FormValues): Observable<SubmittedForm> {
    return this.print(form.id ?? '', values, true).pipe(
      switchMap(({ pdf, reference, signed }) => {
        const file = new File([pdf], pdfName(form, reference), { type: 'application/pdf' });
        return this.metadata.upload(file, form.output?.folderId || '-my-').pipe(map((node: any) => ({ node, reference, signed })));
      }),
      switchMap(({ node, reference, signed }) =>
        this.metadata.schema().pipe(
          catchError(() => of({ available: false, fields: {} })),
          switchMap((schema) => {
            const today = new Date().toLocaleDateString('sv-SE');
            const properties = storedProperties(form, values, reference, today);
            // without the dms model deployed only the title and description can be kept
            const usable = Object.fromEntries(Object.entries(properties).filter(([key]) => schema.available || key.startsWith('cm:')));
            return this.metadata.applyMetadata(node.id, usable).pipe(
              switchMap(() => this.metadata.addTags(node.id, form.output?.tags ?? [])),
              // the PDF is filed even when its properties could not be written
              catchError(() => of(null)),
              switchMap(() => this.recordSubmission(reference, { nodeId: node.id })),
              map(() => ({ nodeId: node.id as string, name: node.name as string, reference, signed }))
            );
          })
        )
      )
    );
  }

  /**
   * Starts the form's approval flow on a filed PDF, as the person who filed
   * it. The steps can route on the answers (see workflowValues). Fails with a
   * {@link FlowProblem}; the PDF stays filed either way, so it can be retried.
   */
  startWorkflow(form: FormDefinition, filed: SubmittedForm, values: FormValues): Observable<LaunchedFlow> {
    return this.workflow.ensureTemplatesLoaded().pipe(
      catchError(() => of(null)),
      switchMap(() => {
        // the form is the gate: whoever may file it starts the flow its administrator chose
        const template = this.workflow.getStartableTemplates().find((t) => t.id === form.output?.workflowId);
        if (!template) {
          return throwError(() => 'missing' as FlowProblem);
        }
        const today = new Date().toLocaleDateString('sv-SE');
        return this.workflow
          .launchTemplate(template, `${form.title} ${filed.reference}`, [filed.nodeId], workflowValues(form, values, filed.reference, today))
          .pipe(
            catchError((error) => throwError(() => (error?.message === NO_ASSIGNEE ? 'noAssignee' : 'failed') as FlowProblem)),
            switchMap((flow) => this.recordSubmission(filed.reference, { processId: flow.process.id }).pipe(map(() => flow)))
          );
      })
    );
  }

  // ── the report ────────────────────────────────────────────────────────

  /** Every filed submission of a form, newest first, as the PDFs printed them (administrators only). */
  submissions(formId: string): Observable<FormSubmission[]> {
    return this.http.get<FormSubmission[]>(`${this.baseUrl}/${encodeURIComponent(formId)}/submissions`, { headers: this.credentials() });
  }

  /** How many filed submissions each form has, by form id (administrators only); empty when they cannot be counted. */
  submissionCounts(): Observable<Record<string, number>> {
    return this.http
      .get<Record<string, number>>(`${this.baseUrl}/submissions/counts`, { headers: this.credentials() })
      .pipe(catchError(() => of({} as Record<string, number>)));
  }

  /**
   * Tells the jobs service which node a submission was filed as, or the flow
   * it started, so the form's report can link to them. A PDF is filed whether
   * or not this is heard; the report only misses it.
   */
  private recordSubmission(reference: string, update: { nodeId?: string; processId?: string }): Observable<unknown> {
    return this.http
      .post(`${this.baseUrl}/submissions/${encodeURIComponent(reference)}`, update, { headers: this.credentials() })
      .pipe(catchError(() => of(null)));
  }

  /** Whether the jobs service can print PDFs right now. */
  health(): Observable<boolean> {
    return this.http.get<{ pdf: boolean }>(`${this.baseUrl}/health`).pipe(
      map((res) => !!res?.pdf),
      catchError(() => of(false))
    );
  }

  // ── helpers ───────────────────────────────────────────────────────────

  private pdf(url: string, body: unknown): Observable<PrintedForm> {
    return this.http.post(url, body, { headers: this.credentials(), observe: 'response', responseType: 'blob' }).pipe(
      map((res) => ({
        pdf: res.body as Blob,
        reference: res.headers.get('X-Form-Reference') ?? '',
        signed: res.headers.get('X-Form-Signed') === 'true'
      })),
      catchError((error: HttpErrorResponse) => from(this.explain(error)).pipe(switchMap((reason) => throwError(() => reason))))
    );
  }

  /** The error body arrives as a Blob, because the request asked for one. */
  private async explain(error: HttpErrorResponse): Promise<FormPrintError> {
    if (error.status === 503 || error.status === 0 || error.status === 502 || error.status === 504) {
      return { kind: 'engine' };
    }
    let body: any = null;
    try {
      body = JSON.parse(await (error.error as Blob).text());
    } catch {
      body = null;
    }
    if (error.status === 422 && Array.isArray(body?.problems)) {
      return {
        kind: 'values',
        problems: Object.fromEntries(body.problems.map((p: { field: string; code: FormProblemCode }) => [p.field, p.code]))
      };
    }
    return { kind: 'other', message: body?.message ?? error.message };
  }

  private read(id: string): Observable<FormDefinition | null> {
    return from(this.nodes.getNodeContent(id) as Promise<any>).pipe(
      switchMap((data: any) => from(typeof data?.text === 'function' ? (data.text() as Promise<string>) : Promise.resolve(String(data ?? '')))),
      map((text) => {
        try {
          const form = JSON.parse(text) as FormDefinition;
          return Array.isArray(form?.sections) ? { ...form, id } : null;
        } catch {
          return null;
        }
      })
    );
  }

  /** The forms folder's id (or a folder inside it), creating it and those above it the first time it is needed. */
  private folderId(folder = FormsService.FOLDER): Observable<string> {
    const segments = folder.split('/');
    const walk = (parentId: string, depth: number): Observable<string> => {
      if (depth === segments.length) {
        return of(parentId);
      }
      const path = segments.slice(0, depth + 1).join('/');
      return from(this.nodes.getNode('-root-', { relativePath: path } as any) as Promise<any>).pipe(
        map((res: any) => res.entry.id as string),
        catchError(() =>
          from(this.nodes.createNode(parentId, { name: segments[depth], nodeType: 'cm:folder' } as any) as Promise<any>).pipe(
            map((res: any) => res.entry.id as string)
          )
        ),
        switchMap((folderId) => walk(folderId, depth + 1))
      );
    };
    return walk('-root-', 0);
  }

  private fileName(form: FormDefinition): string {
    const slug = (form.code || form.title || 'form')
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 50);
    return `${slug || 'form'}-${Date.now().toString(36)}.json`;
  }

  /** The jobs service asks Alfresco on the person's behalf, so their ticket goes along. */
  private credentials(): HttpHeaders {
    const ticket = this.auth.getToken();
    return ticket ? new HttpHeaders({ Authorization: 'Basic ' + btoa(`ROLE_TICKET:${ticket}`) }) : new HttpHeaders();
  }
}
